import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { api } from "../api/client";
import { renderGoogleSignInButton } from "../auth/googleAuth";
import { colors } from "../theme";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const googleButtonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/vendors";

  const resolveErrorMessage = useCallback(
    (err, fallback = "Sign-in failed") => {
      if (err && typeof err.message === "string" && err.message.trim()) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed?.message && typeof parsed.message === "string")
            return parsed.message;
          if (Array.isArray(parsed?.errors) && parsed.errors.length > 0) {
            return parsed.errors.join(", ");
          }
        } catch (_) {
          return err.message;
        }
        return err.message;
      }
      return fallback;
    },
    [],
  );

  const completeLogin = useCallback(
    (res) => {
      const token = res?.data?.token;
      const user = res?.data?.user;
      if (!token) {
        throw new Error("No token returned from server");
      }
      if (!user || !["admin", "encoder", "merchant"].includes(user.userType)) {
        throw new Error("Your account does not have catalog access.");
      }
      localStorage.setItem("token", token);
      localStorage.setItem("catalog_user", JSON.stringify(user));
      navigate(from, { replace: true });
    },
    [from, navigate],
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        await renderGoogleSignInButton(googleButtonRef.current, {
          onSuccess: async (idToken) => {
            if (cancelled) return;
            setError("");
            setLoading(true);
            try {
              // Pass userType so the backend auto-creates *new* sign-ins as
              // merchants instead of the CUSTOMER default — otherwise the
              // userType check below would reject them on first login.
              // Existing users (admin/encoder/merchant) keep their stored
              // type; userType only takes effect on user creation.
              const res = await api.googleSignIn({
                idToken,
                userType: "merchant",
              });
              completeLogin(res);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(err);
              const message = resolveErrorMessage(err, "Google sign-in failed");
              if (!cancelled) setError(message);
            } finally {
              if (!cancelled) setLoading(false);
            }
          },
          onError: (err) => {
            if (cancelled) return;
            // eslint-disable-next-line no-console
            console.error(err);
            const message = resolveErrorMessage(err, "Google sign-in failed");
            setError(message);
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        const message = resolveErrorMessage(err, "Google sign-in failed");
        if (!cancelled) setError(message);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [completeLogin, resolveErrorMessage]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
        py: 6,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          maxWidth: 400,
          width: "100%",
          p: { xs: 3.5, sm: 4.5 },
          borderRadius: 2,
        }}
      >
        <Stack spacing={3.5}>
          <Stack spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: colors.brand.purple,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: 0.5,
              }}
            >
              P
            </Box>
            <Stack spacing={0.5} alignItems="center" textAlign="center">
              <Typography variant="h5" fontWeight={700}>
                PABS Catalog
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in with your Google account to continue.
              </Typography>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box
            ref={googleButtonRef}
            sx={{
              display: "flex",
              justifyContent: "center",
              minHeight: 44,
              pointerEvents: loading ? "none" : "auto",
              opacity: loading ? 0.6 : 1,
              transition: "opacity 150ms ease-out",
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            Access is limited to admins, encoders, and merchant accounts.
          </Typography>
        </Stack>

        {/* Firebase reCAPTCHA host — kept mounted so phone OTP can be
            re-enabled without re-wiring the auth module. */}
        <Box id="firebase-recaptcha-container" sx={{ display: "none" }} />
      </Paper>
    </Box>
  );
}
